/**
 * IMEI rules for device registration and display (ADR 0032, ADR 0034).
 *
 * A Teltonika unit identifies itself with its 15-digit IMEI; FTS keys it as `TLT-<IMEI>`. An IMEI is not a
 * secret (ADR 0032), but the console shows it masked in lists (last 4 digits), like the gateway logs do.
 * Pure helpers shared by the console, the database rule (fts_imei_valid in the migration) and tests.
 */
import { deviceIsTeltonika } from "./domain.js";

/** Teltonika device ids: `TLT-` + the 15-digit IMEI. */
export const TELTONIKA_ID_PREFIX = "TLT-";
export const TELTONIKA_DEFAULT_MODEL = "FMC130";
/** hello.hw the gateway reports and the registration stores (fts_hw_is_teltonika). */
export const TELTONIKA_HW = `teltonika:${TELTONIKA_DEFAULT_MODEL}`;

/** Luhn (mod 10) check over a digit string; the last digit is the check digit. */
export function luhnValid(digits: string): boolean {
  if (!/^\d+$/.test(digits) || digits.length < 2) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = digits.charCodeAt(digits.length - 1 - i) - 48;
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

export type ImeiIssue = "format" | "checksum";

/** Exactly 15 digits with a valid Luhn check digit; null = valid. */
export function imeiIssue(raw: string): ImeiIssue | null {
  const s = raw.trim();
  if (!/^\d{15}$/.test(s)) return "format";
  return luhnValid(s) ? null : "checksum";
}

export const isValidImei = (raw: string): boolean => imeiIssue(raw) === null;

/** The FTS device id of a Teltonika unit. */
export function teltonikaDeviceId(imei: string): string {
  return `${TELTONIKA_ID_PREFIX}${imei.trim()}`;
}

/** The IMEI inside a `TLT-<IMEI>` id, or null. */
export function imeiOfDeviceId(deviceId: string): string | null {
  const m = /^TLT-(\d{15})$/.exec(deviceId);
  return m ? m[1]! : null;
}

/** "•••••••••••1013": only the last 4 digits are shown. Anything that is not an IMEI-like string is returned as is. */
export function maskImei(imei: string | null | undefined): string | null {
  if (imei === null || imei === undefined) return null;
  const s = imei.trim();
  if (!/^\d{8,20}$/.test(s)) return s;
  return `${"•".repeat(s.length - 4)}${s.slice(-4)}`;
}

/** A device id for a list: an embedded IMEI (TLT-<IMEI>) is masked to its last 4 digits ("TLT-•••1013"). */
export function maskDeviceId(deviceId: string): string {
  const imei = imeiOfDeviceId(deviceId);
  return imei ? `${TELTONIKA_ID_PREFIX}•••${imei.slice(-4)}` : deviceId;
}

/**
 * Device family shown in the console. "teltonika": Teltonika unit (gateway, ADR 0032); "mdvr": bridged JT/T 808
 * recorder (ADR 0021); "fts": our own telematics unit (MQTT, ICD v1).
 */
export const DEVICE_FAMILIES = ["fts", "teltonika", "mdvr"] as const;
export type DeviceFamily = (typeof DEVICE_FAMILIES)[number];

export function deviceFamily(d: { deviceId: string; hw: string | null | undefined }): DeviceFamily {
  if (deviceIsTeltonika(d.deviceId, d.hw)) return "teltonika";
  const hw = d.hw?.trim().toLowerCase() ?? "";
  if (d.deviceId.startsWith("MDVR-") || hw.startsWith("jt808") || hw.startsWith("mdvr")) return "mdvr";
  return "fts";
}

/** Teltonika model from hello.hw ("teltonika:FMC130" -> "FMC130"); the default model before the first hello. */
export function teltonikaModel(hw: string | null | undefined): string {
  const h = hw?.trim();
  if (h && h.startsWith("teltonika:") && h.length > 10) return h.slice(10);
  return TELTONIKA_DEFAULT_MODEL;
}

export type ImeiLineError = { line: number; text: string; reason: ImeiIssue | "duplicate" | "too_many_fields" | "iccid" };
export type ImeiRegistration = { deviceId: string; imei: string; iccid: string | null; hw: string };

/**
 * Registration text for Teltonika units: one IMEI per line, optionally `,<ICCID>` (18-22 digits).
 * Every line is validated (15 digits, Luhn); repeated IMEIs are reported.
 */
export function parseImeiLines(text: string): { rows: ImeiRegistration[]; errors: ImeiLineError[] } {
  const rows: ImeiRegistration[] = [];
  const errors: ImeiLineError[] = [];
  const seen = new Set<string>();
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const parts = line.split(/[,;\t]/).map((p) => p.trim());
    const imei = (parts[0] ?? "").replace(/\s+/g, "");
    const iccid = parts[1] ? parts[1].replace(/\s+/g, "") : null;
    const issue = imeiIssue(imei);
    if (parts.length > 2) errors.push({ line: i + 1, text: line, reason: "too_many_fields" });
    else if (issue) errors.push({ line: i + 1, text: line, reason: issue });
    else if (iccid !== null && !/^\d{18,22}$/.test(iccid)) errors.push({ line: i + 1, text: line, reason: "iccid" });
    else if (seen.has(imei)) errors.push({ line: i + 1, text: line, reason: "duplicate" });
    else {
      seen.add(imei);
      rows.push({ deviceId: teltonikaDeviceId(imei), imei, iccid, hw: TELTONIKA_HW });
    }
  });
  return { rows, errors };
}

/**
 * Masks every Teltonika device id (TLT-<IMEI>) inside a text: used for e-mails and report files (ADR 0034).
 * The id itself stays the identifier in the database and the console detail; it is not a secret, but files and
 * mails leave the system and show only the last 4 digits.
 */
export function maskImeisInText(s: string): string {
  return s.replace(/TLT-(\d{11})(\d{4})(?!\d)/g, "TLT-•••$2");
}
